import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { 
  FileText, Receipt, FileCheck, Heart, CreditCard, 
  Wallet, Download, Eye, Share2, Mail, Copy,
  Sparkles, Plus, Trash2, Loader2, Palette,
  AlertTriangle, CheckCircle, XCircle, Info, Bell, Link2, ArrowRight, Save,
  MessageCircle, ExternalLink, RefreshCw, CheckCircle2, Clock
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import axios from "axios";
import ClientSelector from "../components/ClientSelector";
import InventoryItemSelector from "../components/InventoryItemSelector";
import { useLanguage } from "../context/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const presetColors = [
  { name: "Emerald", primary: "#047857", secondary: "#B49248" },
  { name: "Ocean", primary: "#0369A1", secondary: "#F59E0B" },
  { name: "Purple", primary: "#7C3AED", secondary: "#EC4899" },
  { name: "Crimson", primary: "#DC2626", secondary: "#1F2937" },
  { name: "Slate", primary: "#475569", secondary: "#0EA5E9" },
  { name: "Forest", primary: "#166534", secondary: "#CA8A04" },
];

const documentConfig = {
  "payment-invoice": { 
    label: "Payment Invoice", 
    icon: FileText,
    hasItems: true,
    hasTotals: true,
    hasRecipient: true,
    hasDueDate: true,
    fields: ["items", "totals", "notes", "terms"],
    createsAR: true  // Creates Accounts Receivable entry
  },
  invoice: { 
    label: "Invoice", 
    icon: FileText,
    hasItems: true,
    hasTotals: true,
    hasRecipient: true,
    hasDueDate: true,
    fields: ["items", "totals", "notes", "terms"],
    createsAR: true  // Legacy support
  },
  receipt: { 
    label: "Receipt", 
    icon: Receipt,
    hasItems: true,
    hasTotals: true,
    hasRecipient: true,
    fields: ["items", "totals", "payment_method", "notes"]
  },
  quote: { 
    label: "Quote / Proforma / Estimate", 
    icon: FileCheck,
    hasItems: true,
    hasTotals: true,
    hasRecipient: true,
    hasDueDate: true,
    fields: ["items", "totals", "notes", "terms"],
    canConvertToPaymentInvoice: true
  },
};

const templates = ["minimal", "modern", "bold"];
const currencies = ["USD", "EUR", "GBP", "CAD", "AUD", "INR"];

export default function DocumentEditor() {
  const { docType } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const previewRef = useRef(null);
  const { t } = useLanguage();
  const config = documentConfig[docType] || documentConfig["payment-invoice"];
  const Icon = config.icon;
  
  // Get edit ID from query params
  const editDocId = searchParams.get("edit");

  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [template, setTemplate] = useState("minimal");
  const [aiText, setAiText] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  
  const [document, setDocument] = useState({
    meta: {
      doc_type: docType,
      doc_number: "",
      issue_date: new Date().toISOString().split("T")[0],
      due_date: "",
      template_style: "minimal",
      currency: "USD",
      status: "draft"
    },
    issuer: {
      name: "",
      email: "",
      phone: "",
      address: "",
      logo_url: "",
      logo_data: "",
      tax_id: ""
    },
    recipient: {
      name: "",
      email: "",
      phone: "",
      address: "",
      company: ""
    },
    content: {
      title: "",
      subject: "",
      body: "",
      items: [{ description: "", quantity: 1, unit_price: 0, amount: 0 }],
      payment_method: "",
      payment_reference: "",
      purpose: "",
      attendees: [],
      agenda: [],
      action_items: []
    },
    totals: {
      subtotal: 0,
      tax_rate: 0,
      tax_amount: 0,
      discount: 0,
      total: 0
    },
    notes: "",
    terms: "",
    brand_primary_color: "#047857",
    brand_secondary_color: "#B49248",
    signature_data: "",
    client_id: "",
    client_snapshot: null
  });

  // Client state
  const [selectedClientId, setSelectedClientId] = useState("");
  const [showUpdateClientDialog, setShowUpdateClientDialog] = useState(false);
  const [pendingClientUpdate, setPendingClientUpdate] = useState(null);

  // AI Features state
  const [errorCheckResult, setErrorCheckResult] = useState(null);
  const [errorCheckLoading, setErrorCheckLoading] = useState(false);
  const [followUpResult, setFollowUpResult] = useState(null);
  const [followUpLoading, setFollowUpLoading] = useState(false);
  const [followUpChannel, setFollowUpChannel] = useState("email");
  const [chainResult, setChainResult] = useState(null);
  const [showFollowUpDialog, setShowFollowUpDialog] = useState(false);

  // Share & Email state
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailForm, setEmailForm] = useState({ recipient_email: "", recipient_name: "", message: "" });
  const [aiEmailLoading, setAiEmailLoading] = useState(false);
  const [convertLoading, setConvertLoading] = useState(false);
  
  // Payment dialog state
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentType, setPaymentType] = useState("full"); // "full" or "partial"
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [paymentReference, setPaymentReference] = useState("");
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [invoicePaymentInfo, setInvoicePaymentInfo] = useState(null);

  // Load profile on mount or load existing document if editing
  useEffect(() => {
    if (editDocId) {
      // Load existing document from localStorage
      loadExistingDocument(editDocId);
    } else {
      loadProfile();
      loadNextDocNumber();
    }
    loadAutoFillData();
  }, [editDocId]);

  // Load existing document from library for editing
  const loadExistingDocument = async (docId) => {
    try {
      // First try to load from backend
      const response = await axios.get(`${API}/documents/${docId}`);
      if (response.data) {
        setDocument(response.data);
        setIsEditing(true);
        setTemplate(response.data.meta?.template_style || "minimal");
        
        // If it's a payment invoice, load payment history
        if (response.data.meta?.doc_type === "payment-invoice") {
          try {
            const historyResponse = await axios.get(`${API}/invoices/${docId}/payment-history`);
            setPaymentHistory(historyResponse.data.payments || []);
          } catch (histError) {
            console.log("No payment history found");
            setPaymentHistory([]);
          }
        }
        
        toast.success("Document loaded for editing");
        return;
      }
    } catch (error) {
      console.log("Document not found in backend, checking local storage");
    }
    
    // Fallback to localStorage
    const library = JSON.parse(localStorage.getItem("utilityDocs_library") || "[]");
    const existingDoc = library.find(d => d.id === docId);
    
    if (existingDoc) {
      setDocument(existingDoc);
      setIsEditing(true);
      setTemplate(existingDoc.meta?.template_style || "minimal");
      setPaymentHistory([]); // No history for local-only docs
      toast.success("Document loaded for editing");
    } else {
      toast.error("Document not found");
      loadProfile();
      loadNextDocNumber();
    }
  };

  // Load next document number
  const loadNextDocNumber = async () => {
    try {
      const response = await axios.get(`${API}/documents/next-number/${docType}`);
      if (response.data.doc_number) {
        setDocument(prev => ({
          ...prev,
          meta: { ...prev.meta, doc_number: response.data.doc_number }
        }));
      }
    } catch (error) {
      console.error("Failed to load next doc number:", error);
      // Fallback to simple format
      const prefix = docType.substring(0, 3).toUpperCase();
      const fallbackNumber = `${prefix}-${new Date().getFullYear()}-${Date.now().toString().slice(-4)}`;
      setDocument(prev => ({
        ...prev,
        meta: { ...prev.meta, doc_number: fallbackNumber }
      }));
    }
  };

  // Load auto-fill data from sessionStorage
  const loadAutoFillData = () => {
    const autoFillData = sessionStorage.getItem("autoFillData");
    if (autoFillData) {
      try {
        const data = JSON.parse(autoFillData);
        setDocument(prev => ({
          ...prev,
          recipient: data.recipient ? { ...prev.recipient, ...data.recipient } : prev.recipient,
          content: {
            ...prev.content,
            items: data.items && data.items.length > 0 ? data.items.map(item => ({
              description: item.description || "",
              quantity: item.quantity || 1,
              unit_price: item.unit_price || item.amount || 0,
              amount: item.amount || 0
            })) : prev.content.items,
            subject: data.content?.subject || prev.content.subject,
            body: data.content?.body || prev.content.body
          },
          totals: data.totals ? { ...prev.totals, total: data.totals.total || 0 } : prev.totals,
          meta: data.meta ? { ...prev.meta, ...data.meta } : prev.meta,
          notes: data.notes || prev.notes
        }));
        sessionStorage.removeItem("autoFillData");
        toast.success("Auto-filled from AI!");
      } catch (e) {
        console.error("Failed to load auto-fill data:", e);
      }
    }
  };

  // Recalculate totals when items change
  useEffect(() => {
    if (config.hasTotals) {
      calculateTotals();
    }
  }, [document.content.items, document.totals.tax_rate, document.totals.discount]);

  const loadProfile = async () => {
    try {
      const response = await axios.get(`${API}/business-profile`);
      const profile = response.data;
      
      if (profile && (profile.business_name || profile.name)) {
        setDocument(prev => ({
          ...prev,
          issuer: {
            name: profile.business_name || profile.name || "",
            email: profile.email || "",
            phone: profile.phone || "",
            address: profile.address || "",
            logo_url: profile.logo_url || "",
            logo_data: profile.logo_data || "",
            tax_id: profile.tax_id || ""
          },
          meta: {
            ...prev.meta,
            currency: profile.default_currency || "USD"
          },
          totals: {
            ...prev.totals,
            tax_rate: profile.default_tax_rate || 0
          },
          notes: profile.default_footer_note || profile.default_notes || "",
          terms: profile.default_terms || "",
          brand_primary_color: profile.primary_color || profile.brand_primary_color || "#047857",
          brand_secondary_color: profile.brand_secondary_color || "#B49248",
          signature_data: profile.signature_data || ""
        }));
      }
    } catch (error) {
      console.error("Failed to load business profile:", error);
    }
  };

  const calculateTotals = () => {
    const items = document.content.items || [];
    const subtotal = items.reduce((sum, item) => sum + (item.amount || 0), 0);
    const taxAmount = subtotal * (document.totals.tax_rate / 100);
    const total = subtotal + taxAmount - document.totals.discount;
    
    setDocument(prev => ({
      ...prev,
      totals: {
        ...prev.totals,
        subtotal,
        tax_amount: taxAmount,
        total
      }
    }));
  };

  const updateItem = (index, field, value) => {
    const newItems = [...document.content.items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    if (field === "quantity" || field === "unit_price") {
      newItems[index].amount = newItems[index].quantity * newItems[index].unit_price;
    }
    
    setDocument(prev => ({
      ...prev,
      content: { ...prev.content, items: newItems }
    }));
  };

  const addItem = () => {
    setDocument(prev => ({
      ...prev,
      content: {
        ...prev.content,
        items: [...prev.content.items, { description: "", quantity: 1, unit_price: 0, amount: 0 }]
      }
    }));
  };

  const removeItem = (index) => {
    if (document.content.items.length > 1) {
      setDocument(prev => ({
        ...prev,
        content: {
          ...prev.content,
          items: prev.content.items.filter((_, i) => i !== index)
        }
      }));
    }
  };

  const handleAIParse = async () => {
    if (!aiText.trim()) {
      toast.error("Please enter some text to parse");
      return;
    }
    
    setAiLoading(true);
    try {
      const response = await axios.post(`${API}/ai/parse`, {
        text: aiText,
        doc_type: docType,
        parse_type: "items"
      });
      
      if (response.data.success && response.data.data.items) {
        const parsedItems = response.data.data.items.map(item => ({
          description: item.description || "",
          quantity: item.quantity || 1,
          unit_price: item.unit_price || 0,
          amount: (item.quantity || 1) * (item.unit_price || 0)
        }));
        
        setDocument(prev => ({
          ...prev,
          content: { ...prev.content, items: parsedItems }
        }));
        
        toast.success("Items extracted successfully!");
        setAiText("");
      } else {
        toast.error(response.data.message || "Failed to parse text");
      }
    } catch (error) {
      toast.error("AI parsing failed. Please try again.");
      console.error(error);
    } finally {
      setAiLoading(false);
    }
  };

  const generatePDF = async () => {
    if (!previewRef.current) return;
    
    setLoading(true);
    try {
      // Auto-save client if recipient has a name but no client_id selected
      if (document.recipient.name && !selectedClientId && config.hasRecipient) {
        await autoSaveClient();
      }
      
      // Save document to backend and mark as issued (for invoices/quotes)
      const backendId = await saveDocumentToBackend();
      
      const canvas = await html2canvas(previewRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff"
      });
      
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4"
      });
      
      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
      pdf.save(`${docType}-${document.meta.doc_number || "draft"}.pdf`);
      
      // Save to localStorage with the backend ID
      saveToLibrary(backendId);
      
      toast.success("PDF downloaded successfully!");
    } catch (error) {
      toast.error("Failed to generate PDF");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Save document to backend
  const saveDocumentToBackend = async () => {
    try {
      // Prepare document data with client info
      const docData = {
        ...document,
        client_id: selectedClientId || document.client_id,
        client_snapshot: selectedClientId ? {
          client_id: selectedClientId,
          name: document.recipient.name,
          email: document.recipient.email,
          phone: document.recipient.phone,
          address: document.recipient.address,
          company: document.recipient.company
        } : document.client_snapshot,
        meta: {
          ...document.meta,
          doc_type: docType, // Ensure correct doc type
          status: (docType === "invoice" || docType === "quote") ? "issued" : document.meta.status
        }
      };
      
      // Check if document already exists AND is the same type
      let existingDoc = null;
      if (document.id) {
        try {
          const resp = await axios.get(`${API}/documents/${document.id}`);
          // Only use existing doc if it's the same type
          if (resp?.data?.meta?.doc_type === docType) {
            existingDoc = resp.data;
          }
        } catch (e) {
          // Document not found, will create new
        }
      }
      
      if (existingDoc) {
        // Update existing document
        await axios.put(`${API}/documents/${document.id}`, docData);
        return document.id; // Return existing ID
      } else {
        // Create new document - clear any stale ID
        delete docData.id;
        const response = await axios.post(`${API}/documents`, docData);
        // Update local state with generated ID and doc number
        if (response.data.id) {
          setDocument(prev => ({
            ...prev,
            id: response.data.id,
            meta: {
              ...prev.meta,
              doc_number: response.data.meta.doc_number || prev.meta.doc_number,
              status: response.data.meta.status
            }
          }));
          return response.data.id; // Return new ID
        }
      }
    } catch (error) {
      console.error("Failed to save document to backend:", error);
      // Don't block PDF generation if backend save fails
    }
    return null;
  };

  // Auto-save client from document
  const autoSaveClient = async () => {
    if (!document.recipient.name) return;
    
    try {
      const response = await axios.post(`${API}/clients/from-document`, document.recipient);
      
      if (response.data.duplicate_found) {
        // Show duplicate warning - user can choose
        setPendingClientUpdate(response.data.potential_matches[0]);
        setShowUpdateClientDialog(true);
        return;
      }
      
      if (response.data.created && response.data.client) {
        setSelectedClientId(response.data.client.id);
        setDocument(prev => ({ 
          ...prev, 
          client_id: response.data.client.id,
          client_snapshot: {
            client_id: response.data.client.id,
            name: document.recipient.name,
            email: document.recipient.email,
            phone: document.recipient.phone,
            address: document.recipient.address,
            company: document.recipient.company
          }
        }));
        toast.success(`Client "${document.recipient.name}" saved`);
      }
    } catch (error) {
      console.error("Failed to auto-save client:", error);
    }
  };

  const saveToLibrary = (backendId = null) => {
    const library = JSON.parse(localStorage.getItem("utilityDocs_library") || "[]");
    
    // Use backend ID if provided, then existing ID if editing, otherwise use doc_number
    const docId = backendId || (isEditing && document.id 
      ? document.id 
      : (document.meta.doc_number || `${docType}-${Date.now()}`));
    
    const docToSave = {
      ...document,
      id: docId,
      client_id: selectedClientId || document.client_id,
      savedAt: new Date().toISOString()
    };
    
    // Remove duplicates
    const filtered = library.filter(d => d.id !== docToSave.id);
    filtered.unshift(docToSave);
    
    // Keep only last 50 documents
    localStorage.setItem("utilityDocs_library", JSON.stringify(filtered.slice(0, 50)));
  };

  // Handle explicit save draft action
  const handleSaveDraft = async () => {
    setLoading(true);
    try {
      // Save to backend first
      const backendId = await saveDocumentToBackend();
      
      if (backendId) {
        // Update local state with backend ID
        setDocument(prev => ({
          ...prev,
          id: backendId
        }));
        
        // Save to library with backend ID
        saveToLibrary(backendId);
        toast.success(isEditing ? "Draft updated!" : "Draft saved!");
      } else {
        // Fallback to local-only save
        saveToLibrary();
        toast.success(isEditing ? "Draft updated locally!" : "Draft saved locally!");
      }
    } catch (error) {
      console.error("Failed to save draft:", error);
      // Fallback to local-only save
      saveToLibrary();
      toast.success("Draft saved locally!");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: document.meta.currency
    }).format(amount);
  };

  // AI Error Check
  const checkDocumentErrors = async () => {
    setErrorCheckLoading(true);
    setErrorCheckResult(null);
    
    try {
      const response = await axios.post(`${API}/ai/check-errors`, {
        doc_type: docType,
        items: document.content.items,
        totals: document.totals,
        meta: document.meta,
        recipient: document.recipient,
        issuer: document.issuer
      });
      
      setErrorCheckResult(response.data);
      
      if (response.data.errors.length === 0) {
        toast.success("No issues found! Document looks good.");
      } else {
        const errorCount = response.data.errors.filter(e => e.type === "error").length;
        const warningCount = response.data.errors.filter(e => e.type === "warning").length;
        toast.warning(`Found ${errorCount} errors and ${warningCount} warnings`);
      }
    } catch (error) {
      toast.error("Error check failed");
      console.error(error);
    } finally {
      setErrorCheckLoading(false);
    }
  };

  // AI Follow-Up Generator
  const generateFollowUp = async () => {
    setFollowUpLoading(true);
    setFollowUpResult(null);
    
    try {
      const response = await axios.post(`${API}/ai/generate-followup`, {
        doc_type: docType,
        recipient_name: document.recipient.name || "Customer",
        doc_number: document.meta.doc_number || "Draft",
        amount: document.totals.total || 0,
        due_date: document.meta.due_date || "N/A",
        channel: followUpChannel
      });
      
      if (response.data.success) {
        setFollowUpResult(response.data);
      } else {
        toast.error("Failed to generate follow-up");
      }
    } catch (error) {
      toast.error("Follow-up generation failed");
      console.error(error);
    } finally {
      setFollowUpLoading(false);
    }
  };

  // Document Chaining
  const checkDocumentChain = async () => {
    try {
      const response = await axios.post(`${API}/ai/chain-document`, {
        current_doc_type: docType,
        doc_data: {
          recipient: document.recipient,
          issuer: document.issuer,
          totals: document.totals,
          items: document.content.items,
          meta: document.meta
        }
      });
      
      if (response.data.success) {
        setChainResult(response.data);
      } else {
        setChainResult(null);
      }
    } catch (error) {
      console.error("Chain check failed:", error);
      setChainResult(null);
    }
  };

  // Check for chain on mount and when docType changes
  useEffect(() => {
    setChainResult(null); // Clear previous chain result
    checkDocumentChain();
  }, [docType]);

  const navigateToChainedDoc = async () => {
    if (!chainResult) return;
    
    // If this is a quote-type converting to payment invoice, use the proper conversion API
    if (config.canConvertToPaymentInvoice && chainResult.next_doc_type === "payment-invoice") {
      await handleConvertToPaymentInvoice();
      return;
    }
    
    // If this is a Payment Invoice creating a Receipt, use special conversion flow
    if (docType === "payment-invoice" && chainResult.next_doc_type === "receipt") {
      await openPaymentDialog();
      return;
    }
    
    // For other chains, use auto-fill approach
    sessionStorage.setItem("autoFillData", JSON.stringify(chainResult.prefilled_data));
    navigate(`/dashboard/tools/${chainResult.next_doc_type}`);
  };
  
  // Open payment dialog to choose full or partial payment
  const openPaymentDialog = async () => {
    let docId = document.id || editDocId;
    
    // Ensure document is saved first
    const isValidUuid = docId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(docId);
    
    if (!isValidUuid) {
      toast.info("Saving invoice first...");
      const backendId = await saveDocumentToBackend();
      if (backendId) {
        docId = backendId;
        setDocument(prev => ({ ...prev, id: backendId }));
        saveToLibrary(backendId);
      } else {
        toast.error("Please save the invoice first");
        return;
      }
    }
    
    // Fetch payment history and calculate outstanding amount
    try {
      const response = await axios.get(`${API}/invoices/${docId}/payment-history`);
      setPaymentHistory(response.data.payments || []);
      setInvoicePaymentInfo({
        invoice_total: response.data.invoice_total,
        total_paid: response.data.total_paid,
        outstanding_amount: response.data.outstanding_amount,
        status: response.data.status
      });
      
      // Default payment amount to outstanding balance
      setPaymentAmount(response.data.outstanding_amount.toString());
      setPaymentType("full");
      setPaymentMethod("Cash");
      setPaymentReference("");
      setShowPaymentDialog(true);
    } catch (error) {
      console.error("Failed to load payment info:", error);
      // Fallback to document totals
      const invoiceTotal = document.totals?.total || 0;
      const paidAmount = document.meta?.paid_amount || 0;
      const outstanding = invoiceTotal - paidAmount;
      
      setInvoicePaymentInfo({
        invoice_total: invoiceTotal,
        total_paid: paidAmount,
        outstanding_amount: outstanding,
        status: document.meta?.status || "issued"
      });
      setPaymentAmount(outstanding.toString());
      setPaymentType("full");
      setShowPaymentDialog(true);
    }
  };
  
  // Create Receipt from Payment Invoice with full or partial payment
  const handleCreateReceiptFromInvoice = async () => {
    setConvertLoading(true);
    try {
      let docId = document.id || editDocId;
      
      // Ensure source document is saved to backend first
      const isValidUuid = docId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(docId);
      
      if (!isValidUuid) {
        toast.info("Saving invoice before creating receipt...");
        const backendId = await saveDocumentToBackend();
        if (backendId) {
          docId = backendId;
          setDocument(prev => ({ ...prev, id: backendId }));
          saveToLibrary(backendId);
        } else {
          toast.error("Please save the invoice first");
          setConvertLoading(false);
          return;
        }
      }
      
      // Prepare payment data
      const paymentData = {
        source_invoice_id: docId,
        payment_type: paymentType,
        payment_amount: paymentType === "partial" ? parseFloat(paymentAmount) : 0,
        payment_method: paymentMethod,
        payment_reference: paymentReference
      };
      
      // Call the create-receipt endpoint
      const response = await axios.post(`${API}/documents/create-receipt-from-invoice`, paymentData);
      
      const { is_fully_paid, outstanding_amount, payment_amount: paid } = response.data;
      
      if (is_fully_paid) {
        toast.success(`Payment of $${paid.toFixed(2)} recorded - Invoice fully paid!`);
      } else {
        toast.success(`Partial payment of $${paid.toFixed(2)} recorded - $${outstanding_amount.toFixed(2)} outstanding`);
      }
      
      // Save the new Receipt to library
      const newReceipt = response.data.receipt;
      const library = JSON.parse(localStorage.getItem("utilityDocs_library") || "[]");
      library.unshift({
        ...newReceipt,
        savedAt: new Date().toISOString()
      });
      localStorage.setItem("utilityDocs_library", JSON.stringify(library.slice(0, 50)));
      
      // Close dialog
      setShowPaymentDialog(false);
      
      // Navigate to the new Receipt
      navigate(`/dashboard/tools/receipt?edit=${response.data.receipt.id}`);
    } catch (error) {
      console.error("Failed to create receipt:", error);
      toast.error(error.response?.data?.detail || "Failed to create receipt");
    } finally {
      setConvertLoading(false);
    }
  };

  const copyFollowUp = () => {
    if (followUpResult) {
      const text = followUpResult.subject 
        ? `Subject: ${followUpResult.subject}\n\n${followUpResult.message}`
        : followUpResult.message;
      navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard!");
    }
  };

  // Share document
  const handleShare = async () => {
    if (!document.id && !document.meta.doc_number) {
      toast.error("Please save the document first by generating a PDF");
      return;
    }
    
    setShareLoading(true);
    try {
      // First save document if not saved
      if (!document.id) {
        await saveDocumentToBackend();
      }
      
      const docId = document.id || document.meta.doc_number;
      const response = await axios.post(`${API}/documents/${docId}/share`, {
        expires_in_days: 30
      });
      
      setShareUrl(response.data.share_url);
      setShowShareDialog(true);
      toast.success("Share link created!");
    } catch (error) {
      console.error("Failed to create share link:", error);
      toast.error("Failed to create share link. Please generate a PDF first.");
    } finally {
      setShareLoading(false);
    }
  };

  const copyShareUrl = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success("Link copied to clipboard!");
  };

  // Share via WhatsApp
  const shareViaWhatsApp = () => {
    const message = `${document.issuer.name || "Document"}: ${config.label} ${document.meta.doc_number}\n\nView/Download: ${shareUrl}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

  // Share via Email (opens mail client)
  const shareViaEmailClient = () => {
    const subject = `${config.label} ${document.meta.doc_number} from ${document.issuer.name || "LedgerAMT"}`;
    const body = `Dear ${document.recipient.name || "Customer"},\n\nPlease find your ${config.label.toLowerCase()} attached.\n\nView/Download: ${shareUrl}\n\nBest regards,\n${document.issuer.name || "LedgerAMT"}`;
    const mailtoUrl = `mailto:${document.recipient.email || ""}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;
  };

  // Convert Quote to Payment Invoice
  const handleConvertToPaymentInvoice = async () => {
    setConvertLoading(true);
    try {
      let docId = document.id || editDocId;
      
      // If no ID or the ID doesn't look like a UUID, save to backend first
      const isValidUuid = docId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(docId);
      
      if (!isValidUuid) {
        toast.info("Saving document before conversion...");
        const backendId = await saveDocumentToBackend();
        if (backendId) {
          docId = backendId;
          // Update local state with backend ID
          setDocument(prev => ({ ...prev, id: backendId }));
          // Also update library
          saveToLibrary(backendId);
        } else {
          toast.error("Please save the document first");
          setConvertLoading(false);
          return;
        }
      }
      
      const response = await axios.post(`${API}/documents/convert-to-payment-invoice`, {
        source_document_id: docId
      });
      
      toast.success("Converted to Payment Invoice!");
      
      // Save the new Payment Invoice to library
      const newInvoice = response.data.payment_invoice;
      const library = JSON.parse(localStorage.getItem("utilityDocs_library") || "[]");
      library.unshift({
        ...newInvoice,
        savedAt: new Date().toISOString()
      });
      localStorage.setItem("utilityDocs_library", JSON.stringify(library.slice(0, 50)));
      
      // Navigate to the new Payment Invoice
      navigate(`/dashboard/tools/payment-invoice?edit=${response.data.payment_invoice.id}`);
    } catch (error) {
      console.error("Failed to convert:", error);
      toast.error(error.response?.data?.detail || "Failed to convert to Payment Invoice");
    } finally {
      setConvertLoading(false);
    }
  };

  // Generate AI-powered email message based on company profile
  const generateAIEmailMessage = async () => {
    setAiEmailLoading(true);
    try {
      const response = await axios.post(`${API}/ai/generate-email`, {
        doc_type: docType,
        doc_number: document.meta.doc_number,
        recipient_name: emailForm.recipient_name || document.recipient.name || "Customer",
        company_name: document.issuer.name || "Our Company",
        amount: document.totals.total,
        currency: document.meta.currency,
        due_date: document.meta.due_date
      });
      
      setEmailForm(prev => ({
        ...prev,
        message: response.data.message
      }));
      toast.success("AI message generated!");
    } catch (error) {
      console.error("Failed to generate AI email:", error);
      toast.error("Failed to generate AI message");
    } finally {
      setAiEmailLoading(false);
    }
  };

  // Send document via email
  const handleSendEmail = async () => {
    if (!emailForm.recipient_email) {
      toast.error("Please enter recipient email");
      return;
    }
    
    if (!document.id && !document.meta.doc_number) {
      toast.error("Please save the document first by generating a PDF");
      return;
    }
    
    setEmailLoading(true);
    try {
      // First save document if not saved
      if (!document.id) {
        await saveDocumentToBackend();
      }
      
      const docId = document.id || document.meta.doc_number;
      await axios.post(`${API}/documents/${docId}/send-email`, {
        recipient_email: emailForm.recipient_email,
        recipient_name: emailForm.recipient_name || document.recipient.name,
        message: emailForm.message
      });
      
      toast.success("Document sent successfully!");
      setShowEmailDialog(false);
      setEmailForm({ recipient_email: "", recipient_name: "", message: "" });
    } catch (error) {
      console.error("Failed to send email:", error);
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setEmailLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto animate-slide-in" data-testid="document-editor">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground">
              {config.label}
            </h1>
            <p className="text-sm text-muted-foreground">
              #{document.meta.doc_number || "Draft"}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Brand Color Picker */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" data-testid="brand-color-btn">
                <div className="flex mr-1">
                  <div 
                    className="w-4 h-4 rounded-l-sm border border-gray-300" 
                    style={{ backgroundColor: document.brand_primary_color }}
                  />
                  <div 
                    className="w-4 h-4 rounded-r-sm border border-gray-300" 
                    style={{ backgroundColor: document.brand_secondary_color }}
                  />
                </div>
                <span className="text-xs ml-1 hidden sm:inline">Colors</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72" align="end">
              <div className="space-y-4">
                <h4 className="font-heading font-semibold text-sm">Document Colors</h4>
                
                {/* Presets */}
                <div className="flex flex-wrap gap-2">
                  {presetColors.map((preset) => (
                    <button
                      key={preset.name}
                      onClick={() => setDocument(prev => ({
                        ...prev,
                        brand_primary_color: preset.primary,
                        brand_secondary_color: preset.secondary
                      }))}
                      className="flex items-center gap-1.5 px-2 py-1 rounded border border-border hover:border-primary/50 transition-colors text-xs"
                      data-testid={`doc-preset-${preset.name.toLowerCase()}`}
                    >
                      <div className="flex">
                        <div className="w-3 h-3 rounded-l-sm" style={{ backgroundColor: preset.primary }} />
                        <div className="w-3 h-3 rounded-r-sm" style={{ backgroundColor: preset.secondary }} />
                      </div>
                      {preset.name}
                    </button>
                  ))}
                </div>
                
                {/* Custom Colors */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Primary</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="color"
                        value={document.brand_primary_color}
                        onChange={(e) => setDocument(prev => ({ ...prev, brand_primary_color: e.target.value }))}
                        className="w-8 h-8 rounded cursor-pointer border border-border"
                        data-testid="doc-primary-color"
                      />
                      <Input
                        value={document.brand_primary_color}
                        onChange={(e) => setDocument(prev => ({ ...prev, brand_primary_color: e.target.value }))}
                        className="text-xs font-mono h-8"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Secondary</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="color"
                        value={document.brand_secondary_color}
                        onChange={(e) => setDocument(prev => ({ ...prev, brand_secondary_color: e.target.value }))}
                        className="w-8 h-8 rounded cursor-pointer border border-border"
                        data-testid="doc-secondary-color"
                      />
                      <Input
                        value={document.brand_secondary_color}
                        onChange={(e) => setDocument(prev => ({ ...prev, brand_secondary_color: e.target.value }))}
                        className="text-xs font-mono h-8"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          <Select value={template} onValueChange={setTemplate}>
            <SelectTrigger className="w-32" data-testid="template-select">
              <SelectValue placeholder="Template" />
            </SelectTrigger>
            <SelectContent>
              {templates.map(t => (
                <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Error Check Button */}
          <Button 
            variant="outline"
            size="sm"
            onClick={checkDocumentErrors}
            disabled={errorCheckLoading}
            data-testid="error-check-btn"
          >
            {errorCheckLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <AlertTriangle className="w-4 h-4" />
            )}
          </Button>

          {/* Follow-Up Dialog */}
          <Dialog open={showFollowUpDialog} onOpenChange={setShowFollowUpDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid="followup-btn">
                <Bell className="w-4 h-4" />
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Generate Follow-Up Message</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Channel</Label>
                  <Select value={followUpChannel} onValueChange={setFollowUpChannel}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="sms">SMS</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <Button onClick={generateFollowUp} disabled={followUpLoading} className="w-full">
                  {followUpLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Bell className="w-4 h-4 mr-2" />
                      Generate Follow-Up
                    </>
                  )}
                </Button>

                {followUpResult && (
                  <div className="p-4 bg-muted/50 rounded-lg">
                    {followUpResult.subject && (
                      <p className="font-medium text-sm mb-2">Subject: {followUpResult.subject}</p>
                    )}
                    <p className="text-sm whitespace-pre-wrap">{followUpResult.message}</p>
                    <Button variant="outline" size="sm" onClick={copyFollowUp} className="mt-3">
                      Copy to Clipboard
                    </Button>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Share Button */}
          <Button 
            variant="outline"
            size="sm"
            onClick={handleShare}
            disabled={shareLoading}
            data-testid="share-btn"
          >
            {shareLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Share2 className="w-4 h-4" />
            )}
          </Button>

          {/* Email Button */}
          <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid="email-btn">
                <Mail className="w-4 h-4" />
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Send Document via Email</DialogTitle>
                <DialogDescription>
                  Send this {config.label.toLowerCase()} directly to your client from {document.issuer.name || "your company"}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Recipient Email *</Label>
                  <Input
                    type="email"
                    value={emailForm.recipient_email}
                    onChange={(e) => setEmailForm({ ...emailForm, recipient_email: e.target.value })}
                    placeholder="client@example.com"
                    className="mt-1"
                    data-testid="email-recipient-input"
                  />
                </div>
                <div>
                  <Label>Recipient Name</Label>
                  <Input
                    value={emailForm.recipient_name}
                    onChange={(e) => setEmailForm({ ...emailForm, recipient_name: e.target.value })}
                    placeholder={document.recipient.name || "Client name"}
                    className="mt-1"
                    data-testid="email-name-input"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label>Message</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={generateAIEmailMessage}
                      disabled={aiEmailLoading}
                      className="h-7 text-xs gap-1"
                      data-testid="ai-generate-email-btn"
                    >
                      {aiEmailLoading ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Sparkles className="w-3 h-3" />
                      )}
                      AI Generate
                    </Button>
                  </div>
                  <Textarea
                    value={emailForm.message}
                    onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                    placeholder="Click 'AI Generate' to create a professional message based on your company profile..."
                    rows={4}
                    className="mt-1"
                    data-testid="email-message-input"
                  />
                </div>
                <div className="bg-muted/50 rounded-lg p-3 text-xs text-muted-foreground">
                  <p className="font-medium mb-1">Email will be sent from:</p>
                  <p>{document.issuer.name || "LedgerAMT"} via LedgerAMT Platform</p>
                </div>
              </div>
              <DialogFooter className="gap-2 mt-4">
                <Button variant="outline" onClick={() => setShowEmailDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSendEmail} disabled={emailLoading} data-testid="send-email-btn">
                  {emailLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Mail className="w-4 h-4 mr-2" />
                  )}
                  Send Email
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Share Dialog */}
          <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Share Document</DialogTitle>
                <DialogDescription>
                  Share this {config.label.toLowerCase()} via link, WhatsApp, or email
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {/* Share Link */}
                <div>
                  <Label className="text-xs text-muted-foreground">Share Link</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      value={shareUrl}
                      readOnly
                      className="flex-1 font-mono text-sm"
                      data-testid="share-url-input"
                    />
                    <Button variant="outline" size="icon" onClick={copyShareUrl} data-testid="copy-share-btn">
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    This link expires in 30 days
                  </p>
                </div>

                {/* Share Options */}
                <div className="grid grid-cols-2 gap-3">
                  <Button 
                    variant="outline" 
                    className="gap-2"
                    onClick={shareViaWhatsApp}
                    data-testid="share-whatsapp-btn"
                  >
                    <MessageCircle className="w-4 h-4 text-green-500" />
                    WhatsApp
                  </Button>
                  <Button 
                    variant="outline" 
                    className="gap-2"
                    onClick={shareViaEmailClient}
                    data-testid="share-email-client-btn"
                  >
                    <Mail className="w-4 h-4 text-blue-500" />
                    Email
                  </Button>
                </div>

                {/* Preview Link */}
                <div className="pt-2 border-t">
                  <Button 
                    variant="link" 
                    className="text-sm gap-1 p-0 h-auto"
                    onClick={() => window.open(shareUrl, "_blank")}
                  >
                    <ExternalLink className="w-3 h-3" />
                    Preview Document
                  </Button>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={() => setShowShareDialog(false)}>
                  Done
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          {/* Payment Dialog for Full/Partial Payment */}
          <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Record Payment</DialogTitle>
                <DialogDescription>
                  Record full or partial payment for this invoice
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {/* Invoice Summary */}
                {invoicePaymentInfo && (
                  <div className="bg-muted p-4 rounded-lg space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Invoice Total:</span>
                      <span className="font-medium">${invoicePaymentInfo.invoice_total?.toFixed(2)}</span>
                    </div>
                    {invoicePaymentInfo.total_paid > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Previously Paid:</span>
                        <span className="font-medium text-green-600">-${invoicePaymentInfo.total_paid?.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm border-t pt-2">
                      <span className="font-medium">Outstanding Balance:</span>
                      <span className="font-bold text-primary">${invoicePaymentInfo.outstanding_amount?.toFixed(2)}</span>
                    </div>
                  </div>
                )}
                
                {/* Payment History */}
                {paymentHistory.length > 0 && (
                  <div className="bg-muted/50 p-3 rounded-lg">
                    <h4 className="text-xs font-medium mb-2 text-muted-foreground uppercase tracking-wide">Payment History</h4>
                    <div className="space-y-1 max-h-24 overflow-y-auto">
                      {paymentHistory.map((payment, idx) => (
                        <div key={idx} className="flex justify-between text-xs">
                          <span>{payment.receipt_number} - {new Date(payment.created_at).toLocaleDateString()}</span>
                          <span className="font-medium">${payment.payment_amount?.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Payment Type Selection */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Payment Type</label>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      type="button"
                      variant={paymentType === "full" ? "default" : "outline"}
                      onClick={() => {
                        setPaymentType("full");
                        setPaymentAmount(invoicePaymentInfo?.outstanding_amount?.toString() || "0");
                      }}
                      className="w-full"
                      data-testid="payment-type-full"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Full Payment
                    </Button>
                    <Button
                      type="button"
                      variant={paymentType === "partial" ? "default" : "outline"}
                      onClick={() => setPaymentType("partial")}
                      className="w-full"
                      data-testid="payment-type-partial"
                    >
                      <Clock className="w-4 h-4 mr-2" />
                      Partial Payment
                    </Button>
                  </div>
                </div>
                
                {/* Payment Amount (for partial) */}
                {paymentType === "partial" && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Payment Amount</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0.01"
                        max={invoicePaymentInfo?.outstanding_amount || 0}
                        value={paymentAmount}
                        onChange={(e) => setPaymentAmount(e.target.value)}
                        className="pl-7"
                        placeholder="0.00"
                        data-testid="payment-amount-input"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Max: ${invoicePaymentInfo?.outstanding_amount?.toFixed(2)}
                    </p>
                  </div>
                )}
                
                {/* Payment Method */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Payment Method</label>
                  <select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    data-testid="payment-method-select"
                  >
                    <option value="Cash">Cash</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option value="Credit Card">Credit Card</option>
                    <option value="Check">Check</option>
                    <option value="Mobile Money">Mobile Money</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                
                {/* Payment Reference */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Reference / Notes (Optional)</label>
                  <Input
                    value={paymentReference}
                    onChange={(e) => setPaymentReference(e.target.value)}
                    placeholder="e.g., Check #123, Transfer Ref"
                    data-testid="payment-reference-input"
                  />
                </div>
                
                {/* Summary */}
                <div className="bg-primary/5 p-3 rounded-lg border border-primary/20">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Amount to Record:</span>
                    <span className="text-xl font-bold text-primary">
                      ${paymentType === "full" 
                        ? invoicePaymentInfo?.outstanding_amount?.toFixed(2) 
                        : parseFloat(paymentAmount || 0).toFixed(2)}
                    </span>
                  </div>
                  {paymentType === "partial" && parseFloat(paymentAmount) < invoicePaymentInfo?.outstanding_amount && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Remaining after payment: ${(invoicePaymentInfo?.outstanding_amount - parseFloat(paymentAmount || 0)).toFixed(2)}
                    </p>
                  )}
                </div>
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreateReceiptFromInvoice}
                  disabled={convertLoading || (paymentType === "partial" && (!paymentAmount || parseFloat(paymentAmount) <= 0))}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="confirm-payment-btn"
                >
                  {convertLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Receipt className="w-4 h-4 mr-2" />
                  )}
                  Record Payment & Create Receipt
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          {/* Convert to Payment Invoice Button (only for quote type) */}
          {config.canConvertToPaymentInvoice && isEditing && (
            <Button 
              variant="default"
              onClick={handleConvertToPaymentInvoice}
              disabled={convertLoading}
              className="bg-emerald-600 hover:bg-emerald-700 gap-2"
              data-testid="convert-to-invoice-btn"
            >
              {convertLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Convert to Payment Invoice
            </Button>
          )}
          
          {/* Save Draft Button */}
          <Button 
            variant="outline"
            onClick={handleSaveDraft}
            data-testid="save-draft-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {isEditing ? "Update Draft" : "Save Draft"}
          </Button>
          
          <Button 
            onClick={generatePDF} 
            disabled={loading}
            className="bg-secondary hover:bg-secondary/90 text-secondary-foreground animate-pulse-gold"
            data-testid="generate-pdf-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Generate PDF
          </Button>
        </div>
      </div>

      {/* Error Check Results */}
      {errorCheckResult && (
        <div className="mb-6 p-4 bg-card rounded-lg border border-border" data-testid="error-check-results">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              {errorCheckResult.score >= 90 ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : errorCheckResult.score >= 70 ? (
                <AlertTriangle className="w-5 h-5 text-yellow-500" />
              ) : (
                <XCircle className="w-5 h-5 text-red-500" />
              )}
              <span className="font-heading font-semibold">
                Document Score: {errorCheckResult.score}/100
              </span>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setErrorCheckResult(null)}>
              Dismiss
            </Button>
          </div>
          
          {errorCheckResult.errors.length > 0 && (
            <div className="space-y-2">
              {errorCheckResult.errors.map((error, index) => (
                <div 
                  key={index}
                  className={`flex items-start gap-2 text-sm p-2 rounded ${
                    error.type === "error" ? "bg-red-50 text-red-700" :
                    error.type === "warning" ? "bg-yellow-50 text-yellow-700" :
                    "bg-blue-50 text-blue-700"
                  }`}
                >
                  {error.type === "error" ? <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> :
                   error.type === "warning" ? <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" /> :
                   <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />}
                  <span>{error.message}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Document Chain Suggestion */}
      {chainResult && (
        <div className="mb-6 p-4 bg-primary/5 rounded-lg border border-primary/20 flex items-center justify-between" data-testid="chain-suggestion">
          <div className="flex items-center gap-3">
            <Link2 className="w-5 h-5 text-primary" />
            <div>
              <p className="font-medium text-sm">Continue</p>
              <p className="text-xs text-muted-foreground">
                Create {chainResult.next_doc_label} from this {config.label}
              </p>
            </div>
          </div>
          <Button size="sm" onClick={navigateToChainedDoc} data-testid="chain-create-btn">
            Create {chainResult.next_doc_label} <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}

      {/* Main Content - Split View */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form Section */}
        <div className="space-y-6">
          <Tabs defaultValue="details" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
              <TabsTrigger value="content" data-testid="tab-content">Content</TabsTrigger>
              <TabsTrigger value="branding" data-testid="tab-branding">Branding</TabsTrigger>
              <TabsTrigger value="ai" data-testid="tab-ai">
                <Sparkles className="w-4 h-4 mr-1" /> AI
              </TabsTrigger>
            </TabsList>
            
            {/* Details Tab */}
            <TabsContent value="details" className="space-y-4 mt-4">
              {/* Document Info */}
              <div className="bg-card rounded-lg border border-border p-4 space-y-4">
                <h3 className="font-heading font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  Document Info
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Document Number</Label>
                    <Input
                      value={document.meta.doc_number}
                      onChange={(e) => setDocument(prev => ({
                        ...prev,
                        meta: { ...prev.meta, doc_number: e.target.value }
                      }))}
                      placeholder="Auto-generated"
                      data-testid="doc-number-input"
                    />
                  </div>
                  <div>
                    <Label>Currency</Label>
                    <Select 
                      value={document.meta.currency} 
                      onValueChange={(v) => setDocument(prev => ({
                        ...prev,
                        meta: { ...prev.meta, currency: v }
                      }))}
                    >
                      <SelectTrigger data-testid="currency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {currencies.map(c => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Issue Date</Label>
                    <Input
                      type="date"
                      value={document.meta.issue_date}
                      onChange={(e) => setDocument(prev => ({
                        ...prev,
                        meta: { ...prev.meta, issue_date: e.target.value }
                      }))}
                      data-testid="issue-date-input"
                    />
                  </div>
                  {config.hasDueDate && (
                    <div>
                      <Label>Due Date</Label>
                      <Input
                        type="date"
                        value={document.meta.due_date}
                        onChange={(e) => setDocument(prev => ({
                          ...prev,
                          meta: { ...prev.meta, due_date: e.target.value }
                        }))}
                        data-testid="due-date-input"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Issuer Info */}
              <div className="bg-card rounded-lg border border-border p-4 space-y-4">
                <h3 className="font-heading font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  Your Business
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Business Name</Label>
                    <Input
                      value={document.issuer.name}
                      onChange={(e) => setDocument(prev => ({
                        ...prev,
                        issuer: { ...prev.issuer, name: e.target.value }
                      }))}
                      placeholder="Your Company Name"
                      data-testid="issuer-name-input"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={document.issuer.email}
                      onChange={(e) => setDocument(prev => ({
                        ...prev,
                        issuer: { ...prev.issuer, email: e.target.value }
                      }))}
                      placeholder="email@company.com"
                      data-testid="issuer-email-input"
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={document.issuer.phone}
                      onChange={(e) => setDocument(prev => ({
                        ...prev,
                        issuer: { ...prev.issuer, phone: e.target.value }
                      }))}
                      placeholder="+1 234 567 8900"
                      data-testid="issuer-phone-input"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Address</Label>
                    <Textarea
                      value={document.issuer.address}
                      onChange={(e) => setDocument(prev => ({
                        ...prev,
                        issuer: { ...prev.issuer, address: e.target.value }
                      }))}
                      placeholder="123 Business St, City, Country"
                      rows={2}
                      data-testid="issuer-address-input"
                    />
                  </div>
                </div>
              </div>

              {/* Recipient Info */}
              {config.hasRecipient && (
                <div className="bg-card rounded-lg border border-border p-4 space-y-4">
                  <h3 className="font-heading font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                    {docType === "donation-receipt" ? "Donor" : "Client"}
                  </h3>
                  
                  {/* Client Selector */}
                  <ClientSelector
                    value={selectedClientId}
                    onChange={(clientId) => {
                      setSelectedClientId(clientId);
                      setDocument(prev => ({ ...prev, client_id: clientId }));
                    }}
                    onRecipientChange={(recipientData) => {
                      setDocument(prev => ({
                        ...prev,
                        recipient: { ...prev.recipient, ...recipientData }
                      }));
                    }}
                  />
                  
                  {/* Client Details (auto-filled or manual) */}
                  <div className="grid grid-cols-2 gap-4 pt-2 border-t border-border/50">
                    <div>
                      <Label className="text-xs text-muted-foreground">Name</Label>
                      <Input
                        value={document.recipient.name}
                        onChange={(e) => setDocument(prev => ({
                          ...prev,
                          recipient: { ...prev.recipient, name: e.target.value }
                        }))}
                        placeholder="Client Name"
                        data-testid="recipient-name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Company</Label>
                      <Input
                        value={document.recipient.company}
                        onChange={(e) => setDocument(prev => ({
                          ...prev,
                          recipient: { ...prev.recipient, company: e.target.value }
                        }))}
                        placeholder="Company Name"
                        data-testid="recipient-company-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Email</Label>
                      <Input
                        type="email"
                        value={document.recipient.email}
                        onChange={(e) => setDocument(prev => ({
                          ...prev,
                          recipient: { ...prev.recipient, email: e.target.value }
                        }))}
                        placeholder="client@example.com"
                        data-testid="recipient-email-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Phone</Label>
                      <Input
                        value={document.recipient.phone}
                        onChange={(e) => setDocument(prev => ({
                          ...prev,
                          recipient: { ...prev.recipient, phone: e.target.value }
                        }))}
                        placeholder="+1 234 567 8900"
                        data-testid="recipient-phone-input"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs text-muted-foreground">Address</Label>
                      <Textarea
                        value={document.recipient.address}
                        onChange={(e) => setDocument(prev => ({
                          ...prev,
                          recipient: { ...prev.recipient, address: e.target.value }
                        }))}
                        placeholder="Client address"
                        rows={2}
                        data-testid="recipient-address-input"
                      />
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Content Tab */}
            <TabsContent value="content" className="space-y-4 mt-4">
              {/* Line Items */}
              {config.hasItems && (
                <div className="bg-card rounded-lg border border-border p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-heading font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                      Line Items
                    </h3>
                    <div className="flex gap-2">
                      <InventoryItemSelector
                        onSelect={(inventoryItem) => {
                          setDocument(prev => ({
                            ...prev,
                            content: {
                              ...prev.content,
                              items: [
                                ...prev.content.items,
                                {
                                  description: inventoryItem.description,
                                  quantity: inventoryItem.quantity,
                                  unit_price: inventoryItem.unit_price,
                                  amount: inventoryItem.amount,
                                  inventory_item_id: inventoryItem.inventory_item_id
                                }
                              ]
                            }
                          }));
                        }}
                      />
                      <Button size="sm" variant="outline" onClick={addItem} data-testid="add-item-btn">
                        <Plus className="w-4 h-4 mr-1" /> Add Item
                      </Button>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    {document.content.items.map((item, index) => (
                      <div key={index} className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-5">
                          {index === 0 && <Label className="text-xs">Description</Label>}
                          <Input
                            value={item.description}
                            onChange={(e) => updateItem(index, "description", e.target.value)}
                            placeholder="Item description"
                            data-testid={`item-desc-${index}`}
                          />
                        </div>
                        <div className="col-span-2">
                          {index === 0 && <Label className="text-xs">Qty</Label>}
                          <Input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateItem(index, "quantity", parseFloat(e.target.value) || 0)}
                            data-testid={`item-qty-${index}`}
                          />
                        </div>
                        <div className="col-span-2">
                          {index === 0 && <Label className="text-xs">Price</Label>}
                          <Input
                            type="number"
                            value={item.unit_price}
                            onChange={(e) => updateItem(index, "unit_price", parseFloat(e.target.value) || 0)}
                            data-testid={`item-price-${index}`}
                          />
                        </div>
                        <div className="col-span-2">
                          {index === 0 && <Label className="text-xs">Amount</Label>}
                          <Input
                            value={formatCurrency(item.amount)}
                            readOnly
                            className="bg-muted"
                            data-testid={`item-amount-${index}`}
                          />
                        </div>
                        <div className="col-span-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeItem(index)}
                            disabled={document.content.items.length === 1}
                            data-testid={`remove-item-${index}`}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Totals */}
              {config.hasTotals && (
                <div className="bg-card rounded-lg border border-border p-4 space-y-4">
                  <h3 className="font-heading font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                    Totals
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Tax Rate (%)</Label>
                      <Input
                        type="number"
                        value={document.totals.tax_rate}
                        onChange={(e) => setDocument(prev => ({
                          ...prev,
                          totals: { ...prev.totals, tax_rate: parseFloat(e.target.value) || 0 }
                        }))}
                        data-testid="tax-rate-input"
                      />
                    </div>
                    <div>
                      <Label>Discount</Label>
                      <Input
                        type="number"
                        value={document.totals.discount}
                        onChange={(e) => setDocument(prev => ({
                          ...prev,
                          totals: { ...prev.totals, discount: parseFloat(e.target.value) || 0 }
                        }))}
                        data-testid="discount-input"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Payment Info */}
              {(docType === "receipt" || docType === "proof-of-payment") && (
                <div className="bg-card rounded-lg border border-border p-4 space-y-4">
                  <h3 className="font-heading font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                    Payment Details
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Payment Method</Label>
                      <Input
                        value={document.content.payment_method}
                        onChange={(e) => setDocument(prev => ({
                          ...prev,
                          content: { ...prev.content, payment_method: e.target.value }
                        }))}
                        placeholder="Bank Transfer, Cash, etc."
                        data-testid="payment-method-input"
                      />
                    </div>
                    <div>
                      <Label>Reference Number</Label>
                      <Input
                        value={document.content.payment_reference}
                        onChange={(e) => setDocument(prev => ({
                          ...prev,
                          content: { ...prev.content, payment_reference: e.target.value }
                        }))}
                        placeholder="Transaction ID"
                        data-testid="payment-ref-input"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Donation Purpose */}
              {docType === "donation-receipt" && (
                <div className="bg-card rounded-lg border border-border p-4 space-y-4">
                  <h3 className="font-heading font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                    Donation Details
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <Label>Purpose / Fund</Label>
                      <Input
                        value={document.content.purpose}
                        onChange={(e) => setDocument(prev => ({
                          ...prev,
                          content: { ...prev.content, purpose: e.target.value }
                        }))}
                        placeholder="General Fund, Building Fund, etc."
                        data-testid="donation-purpose-input"
                      />
                    </div>
                    <div>
                      <Label>Donation Amount</Label>
                      <Input
                        type="number"
                        value={document.totals.total}
                        onChange={(e) => setDocument(prev => ({
                          ...prev,
                          totals: { ...prev.totals, total: parseFloat(e.target.value) || 0 }
                        }))}
                        data-testid="donation-amount-input"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Letter Content */}
              {docType === "letter" && (
                <div className="bg-card rounded-lg border border-border p-4 space-y-4">
                  <h3 className="font-heading font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                    Letter Content
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <Label>Subject</Label>
                      <Input
                        value={document.content.subject}
                        onChange={(e) => setDocument(prev => ({
                          ...prev,
                          content: { ...prev.content, subject: e.target.value }
                        }))}
                        placeholder="Subject line"
                        data-testid="letter-subject-input"
                      />
                    </div>
                    <div>
                      <Label>Body</Label>
                      <Textarea
                        value={document.content.body}
                        onChange={(e) => setDocument(prev => ({
                          ...prev,
                          content: { ...prev.content, body: e.target.value }
                        }))}
                        placeholder="Write your letter content here..."
                        rows={8}
                        data-testid="letter-body-input"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Meeting Minutes */}
              {docType === "meeting-minutes" && (
                <div className="bg-card rounded-lg border border-border p-4 space-y-4">
                  <h3 className="font-heading font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                    Meeting Details
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <Label>Meeting Title</Label>
                      <Input
                        value={document.content.title}
                        onChange={(e) => setDocument(prev => ({
                          ...prev,
                          content: { ...prev.content, title: e.target.value }
                        }))}
                        placeholder="Q4 Review Meeting"
                        data-testid="meeting-title-input"
                      />
                    </div>
                    <div>
                      <Label>Attendees (comma-separated)</Label>
                      <Input
                        value={document.content.attendees.join(", ")}
                        onChange={(e) => setDocument(prev => ({
                          ...prev,
                          content: { ...prev.content, attendees: e.target.value.split(",").map(s => s.trim()) }
                        }))}
                        placeholder="John, Jane, Bob"
                        data-testid="meeting-attendees-input"
                      />
                    </div>
                    <div>
                      <Label>Minutes / Notes</Label>
                      <Textarea
                        value={document.content.body}
                        onChange={(e) => setDocument(prev => ({
                          ...prev,
                          content: { ...prev.content, body: e.target.value }
                        }))}
                        placeholder="Meeting discussion and notes..."
                        rows={6}
                        data-testid="meeting-notes-input"
                      />
                    </div>
                    <div>
                      <Label>Action Items (one per line)</Label>
                      <Textarea
                        value={document.content.action_items.join("\n")}
                        onChange={(e) => setDocument(prev => ({
                          ...prev,
                          content: { ...prev.content, action_items: e.target.value.split("\n").filter(s => s.trim()) }
                        }))}
                        placeholder="Follow up with client&#10;Send report by Friday"
                        rows={4}
                        data-testid="meeting-actions-input"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Notes & Terms */}
              <div className="bg-card rounded-lg border border-border p-4 space-y-4">
                <h3 className="font-heading font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  Notes & Terms
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label>Notes</Label>
                    <Textarea
                      value={document.notes}
                      onChange={(e) => setDocument(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Additional notes..."
                      rows={3}
                      data-testid="notes-input"
                    />
                  </div>
                  {(docType === "invoice" || docType === "quote") && (
                    <div>
                      <Label>Terms & Conditions</Label>
                      <Textarea
                        value={document.terms}
                        onChange={(e) => setDocument(prev => ({ ...prev, terms: e.target.value }))}
                        placeholder="Payment terms, conditions..."
                        rows={3}
                        data-testid="terms-input"
                      />
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Branding Tab */}
            <TabsContent value="branding" className="space-y-4 mt-4">
              {/* Logo Upload */}
              <div className="bg-card rounded-lg border border-border p-4 space-y-4">
                <h3 className="font-heading font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  Your Logo
                </h3>
                
                {/* Logo Preview */}
                {(document.issuer.logo_data || document.issuer.logo_url) && (
                  <div className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg">
                    <img 
                      src={document.issuer.logo_data || document.issuer.logo_url} 
                      alt="Logo" 
                      className="h-16 w-auto max-w-[160px] object-contain"
                    />
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setDocument(prev => ({
                        ...prev,
                        issuer: { ...prev.issuer, logo_data: "", logo_url: "" }
                      }))}
                      className="text-destructive hover:text-destructive"
                      data-testid="remove-doc-logo-btn"
                    >
                      Remove
                    </Button>
                  </div>
                )}

                <div className="flex flex-col gap-3">
                  <div>
                    <Label className="text-xs">Upload Logo</Label>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file && file.size <= 500000) {
                          const reader = new FileReader();
                          reader.onload = (event) => {
                            setDocument(prev => ({
                              ...prev,
                              issuer: { ...prev.issuer, logo_data: event.target.result, logo_url: "" }
                            }));
                            toast.success("Logo uploaded!");
                          };
                          reader.readAsDataURL(file);
                        } else if (file) {
                          toast.error("Logo must be under 500KB");
                        }
                      }}
                      className="mt-1"
                      data-testid="doc-logo-upload"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Or paste logo URL</Label>
                    <Input
                      type="url"
                      value={document.issuer.logo_url}
                      onChange={(e) => setDocument(prev => ({
                        ...prev,
                        issuer: { ...prev.issuer, logo_url: e.target.value, logo_data: "" }
                      }))}
                      placeholder="https://example.com/logo.png"
                      className="mt-1"
                      data-testid="doc-logo-url"
                    />
                  </div>
                </div>
              </div>

              {/* Brand Colors */}
              <div className="bg-card rounded-lg border border-border p-4 space-y-4">
                <h3 className="font-heading font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  Document Colors
                </h3>
                
                {/* Presets */}
                <div>
                  <Label className="text-xs">Quick Presets</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {presetColors.map((preset) => (
                      <button
                        key={preset.name}
                        onClick={() => setDocument(prev => ({
                          ...prev,
                          brand_primary_color: preset.primary,
                          brand_secondary_color: preset.secondary
                        }))}
                        className="flex items-center gap-1.5 px-2 py-1.5 rounded border border-border hover:border-primary/50 transition-colors text-xs"
                        data-testid={`branding-preset-${preset.name.toLowerCase()}`}
                      >
                        <div className="flex">
                          <div className="w-4 h-4 rounded-l-sm" style={{ backgroundColor: preset.primary }} />
                          <div className="w-4 h-4 rounded-r-sm" style={{ backgroundColor: preset.secondary }} />
                        </div>
                        {preset.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom Colors */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">Primary Color</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="color"
                        value={document.brand_primary_color}
                        onChange={(e) => setDocument(prev => ({ ...prev, brand_primary_color: e.target.value }))}
                        className="w-10 h-10 rounded cursor-pointer border border-border"
                        data-testid="branding-primary-color"
                      />
                      <Input
                        value={document.brand_primary_color}
                        onChange={(e) => setDocument(prev => ({ ...prev, brand_primary_color: e.target.value }))}
                        className="font-mono text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Secondary Color</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="color"
                        value={document.brand_secondary_color}
                        onChange={(e) => setDocument(prev => ({ ...prev, brand_secondary_color: e.target.value }))}
                        className="w-10 h-10 rounded cursor-pointer border border-border"
                        data-testid="branding-secondary-color"
                      />
                      <Input
                        value={document.brand_secondary_color}
                        onChange={(e) => setDocument(prev => ({ ...prev, brand_secondary_color: e.target.value }))}
                        className="font-mono text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Signature */}
              <div className="bg-card rounded-lg border border-border p-4 space-y-4">
                <h3 className="font-heading font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  E-Signature
                </h3>
                
                {document.signature_data ? (
                  <div className="space-y-3">
                    <div className="p-3 bg-white rounded border border-border flex items-center justify-between">
                      <img 
                        src={document.signature_data} 
                        alt="Your signature" 
                        className="h-12 object-contain"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDocument(prev => ({ ...prev, signature_data: "" }))}
                        className="text-destructive hover:text-destructive"
                        data-testid="remove-doc-signature-btn"
                      >
                        Remove
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Signature will appear at the bottom of your document
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-muted-foreground mb-3">
                      No signature set. Create one in Settings.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.location.href = "/dashboard/settings"}
                      data-testid="go-to-signature-settings"
                    >
                      Go to Settings
                    </Button>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* AI Tab */}
            <TabsContent value="ai" className="space-y-4 mt-4">
              <div className="bg-card rounded-lg border border-border p-4 space-y-4">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-secondary" />
                  <h3 className="font-heading font-semibold">AI Assistant</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Paste text containing item descriptions, quantities, and prices. 
                  AI will extract and format them as line items.
                </p>
                <Textarea
                  value={aiText}
                  onChange={(e) => setAiText(e.target.value)}
                  placeholder={`Example:\n10 hours consulting at $100/hr\n5 website pages at $200 each\nLogo design - $500`}
                  rows={6}
                  data-testid="ai-text-input"
                />
                <Button 
                  onClick={handleAIParse} 
                  disabled={aiLoading || !aiText.trim()}
                  className="w-full"
                  data-testid="ai-parse-btn"
                >
                  {aiLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Parsing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Extract Items with AI
                    </>
                  )}
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Preview Section */}
        <div className="lg:sticky lg:top-6 lg:self-start">
          <div className="bg-muted/30 rounded-lg p-4">
            <h3 className="font-heading font-semibold text-sm text-muted-foreground uppercase tracking-wide mb-4">
              Preview
            </h3>
            <div 
              ref={previewRef}
              className={`bg-white shadow-2xl border border-gray-200 min-h-[600px] relative overflow-hidden ${
                template === "bold" ? "font-heading" : ""
              }`}
              style={{ aspectRatio: "1/1.414" }}
              data-testid="document-preview"
            >
              {/* PAID IN FULL / PARTIAL PAYMENT Watermark for Receipts */}
              {docType === "receipt" && (document.meta?.paid_in_full || document.meta?.source_invoice_id) && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                  <div 
                    className={`transform -rotate-45 text-6xl font-black tracking-widest opacity-10 select-none`}
                    style={{ color: document.meta?.paid_in_full ? (document.brand_primary_color || "#047857") : "#F59E0B" }}
                  >
                    {document.meta?.paid_in_full ? "PAID IN FULL" : "PARTIAL PAYMENT"}
                  </div>
                </div>
              )}
              
              {/* Top Color Bar for Modern/Bold */}
              {template !== "minimal" && (
                <div 
                  className="h-2"
                  style={{ backgroundColor: document.brand_primary_color }}
                />
              )}
              
              <div className="p-8">
                {/* Document Header */}
                <div className={`flex justify-between items-start mb-8 ${
                  template === "bold" ? "border-b-4 pb-6" : 
                  template === "modern" ? "border-b-2 pb-6" : "pb-4"
                }`}
                style={template === "bold" ? { borderColor: document.brand_primary_color } : { borderColor: "#e5e7eb" }}
                >
                  <div className="flex items-start gap-4">
                    {/* Logo */}
                    {(document.issuer.logo_data || document.issuer.logo_url) ? (
                      <img 
                        src={document.issuer.logo_data || document.issuer.logo_url}
                        alt="Logo"
                        className="h-16 w-auto max-w-[140px] object-contain"
                        crossOrigin="anonymous"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : null}
                    <div>
                      <p className="text-sm font-medium text-gray-800">{document.issuer.name || "Your Business"}</p>
                      {document.issuer.address && (
                        <p className="text-xs text-gray-500 whitespace-pre-line mt-0.5">{document.issuer.address}</p>
                      )}
                      {document.issuer.phone && <p className="text-xs text-gray-500">{document.issuer.phone}</p>}
                    </div>
                  </div>
                  <div className="text-right">
                    <h1 
                      className={`font-bold uppercase tracking-wider ${
                        template === "bold" ? "text-2xl" : 
                        template === "modern" ? "text-xl" : "text-lg"
                      }`}
                      style={{ color: document.brand_primary_color }}
                    >
                      {config.label}
                    </h1>
                    <p className="text-sm font-medium text-gray-700 mt-1">
                      #{document.meta.doc_number || "DRAFT"}
                    </p>
                    {/* PAID IN FULL / PARTIAL Badge for Receipts */}
                    {docType === "receipt" && (document.meta?.paid_in_full !== undefined || document.meta?.source_invoice_id) && (
                      <div 
                        className="inline-block mt-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide"
                        style={{ 
                          backgroundColor: document.meta?.paid_in_full 
                            ? (document.brand_primary_color || "#047857")
                            : "#F59E0B",
                          color: "#ffffff" 
                        }}
                      >
                        {document.meta?.paid_in_full ? "✓ PAID IN FULL" : "◐ PARTIAL PAYMENT"}
                      </div>
                    )}
                    {/* Reference Invoice Number */}
                    {docType === "receipt" && (document.meta?.source_invoice_number || document.meta?.source_document_number) && (
                      <p className="text-xs text-gray-500 mt-1">
                        Ref: {document.meta.source_document_number || document.meta.source_invoice_number}
                      </p>
                    )}
                    <div className="text-xs text-gray-500 mt-2">
                      <p>Date: {document.meta.issue_date}</p>
                      {document.meta.due_date && (
                        <p className="text-red-600 font-medium">Due: {document.meta.due_date}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Bill To Section */}
                {config.hasRecipient && (
                  <div className="mb-6">
                    <div 
                      className="inline-block px-3 py-1 rounded text-xs font-semibold uppercase tracking-wide mb-2"
                      style={{ 
                        backgroundColor: `${document.brand_primary_color}15`,
                        color: document.brand_primary_color 
                      }}
                    >
                      {docType === "donation-receipt" ? "Donor" : docType === "receipt" ? "Received From" : "Bill To"}
                    </div>
                    <div className="text-sm">
                      <p className="font-semibold text-gray-800">{document.recipient.name || "Client Name"}</p>
                      {document.recipient.company && (
                        <p className="text-gray-600">{document.recipient.company}</p>
                      )}
                      {document.recipient.address && (
                        <p className="text-gray-500 whitespace-pre-line text-xs">{document.recipient.address}</p>
                      )}
                      {document.recipient.email && <p className="text-gray-500 text-xs">{document.recipient.email}</p>}
                    </div>
                  </div>
                )}

              {/* Items Table */}
              {config.hasItems && document.content.items.length > 0 && (
                <div className="mb-6">
                  <table className="w-full text-sm">
                    <thead>
                      <tr 
                        style={{ 
                          backgroundColor: document.brand_primary_color,
                          color: "white"
                        }}
                      >
                        <th className="text-left py-2 px-3 font-medium text-xs uppercase tracking-wide">Description</th>
                        <th className="text-center py-2 px-2 font-medium text-xs uppercase tracking-wide w-16">Qty</th>
                        <th className="text-right py-2 px-2 font-medium text-xs uppercase tracking-wide w-24">Rate</th>
                        <th className="text-right py-2 px-3 font-medium text-xs uppercase tracking-wide w-24">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {document.content.items.map((item, index) => (
                        <tr 
                          key={index} 
                          className={index % 2 === 0 ? "bg-gray-50" : "bg-white"}
                        >
                          <td className="py-2.5 px-3 text-gray-800">{item.description || "-"}</td>
                          <td className="text-center py-2.5 px-2 text-gray-600">{item.quantity}</td>
                          <td className="text-right py-2.5 px-2 text-gray-600">{formatCurrency(item.unit_price)}</td>
                          <td className="text-right py-2.5 px-3 font-medium text-gray-800">{formatCurrency(item.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Totals */}
              {config.hasTotals && (
                <div className="flex justify-end mb-6">
                  <div className="w-56 text-sm">
                    <div className="flex justify-between py-1.5 border-b border-gray-100">
                      <span className="text-gray-500">Subtotal</span>
                      <span className="text-gray-800">{formatCurrency(document.totals.subtotal)}</span>
                    </div>
                    {document.totals.tax_rate > 0 && (
                      <div className="flex justify-between py-1.5 border-b border-gray-100">
                        <span className="text-gray-500">Tax ({document.totals.tax_rate}%)</span>
                        <span className="text-gray-800">{formatCurrency(document.totals.tax_amount)}</span>
                      </div>
                    )}
                    {document.totals.discount > 0 && (
                      <div className="flex justify-between py-1.5 border-b border-gray-100">
                        <span className="text-gray-500">Discount</span>
                        <span className="text-red-600">-{formatCurrency(document.totals.discount)}</span>
                      </div>
                    )}
                    <div 
                      className="flex justify-between py-2.5 mt-1 rounded"
                      style={{ 
                        backgroundColor: `${document.brand_primary_color}15`
                      }}
                    >
                      <span 
                        className="font-bold px-2"
                        style={{ color: document.brand_primary_color }}
                      >
                        TOTAL
                      </span>
                      <span 
                        className="font-bold text-lg px-2"
                        style={{ color: document.brand_primary_color }}
                      >
                        {formatCurrency(document.totals.total)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Balance Summary Panel for Payment Invoices */}
              {docType === "payment-invoice" && (document.meta?.paid_amount > 0 || document.meta?.status === "partial" || document.meta?.status === "paid") && (
                <div className="mb-4 border rounded-lg overflow-hidden text-xs" style={{ borderColor: document.meta?.status === "paid" ? "#10B981" : "#F59E0B" }}>
                  {/* Status Header */}
                  <div 
                    className="px-3 py-1.5 text-white text-center text-xs font-bold uppercase tracking-wide"
                    style={{ 
                      backgroundColor: document.meta?.status === "paid" ? "#10B981" : 
                                       document.meta?.status === "partial" ? "#F59E0B" : "#6B7280"
                    }}
                  >
                    {document.meta?.status === "paid" ? "✓ PAID IN FULL" : 
                     document.meta?.status === "partial" ? "◐ PARTIALLY PAID" : "UNPAID"}
                  </div>
                  
                  {/* Balance Summary */}
                  <div className="p-3 bg-gray-50 space-y-1">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Original Amount:</span>
                      <span className="font-medium">{formatCurrency(document.totals?.total || 0)}</span>
                    </div>
                    {(document.meta?.paid_amount || 0) > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Payments Applied:</span>
                        <span className="font-medium text-green-600">−{formatCurrency(document.meta?.paid_amount || 0)}</span>
                      </div>
                    )}
                    <div className="flex justify-between pt-1 border-t border-gray-300">
                      <span className="font-semibold">Balance Due:</span>
                      <span 
                        className="font-bold"
                        style={{ color: (document.meta?.outstanding_amount || 0) > 0 ? "#DC2626" : "#10B981" }}
                      >
                        {formatCurrency(document.meta?.outstanding_amount || (document.totals?.total - (document.meta?.paid_amount || 0)) || 0)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Payment Applied Message */}
                  {document.meta?.status === "partial" && document.meta?.paid_amount > 0 && (
                    <div className="px-3 py-2 bg-amber-50 border-t border-amber-200 text-xs">
                      <p className="text-amber-800">
                        Balance reduced from {formatCurrency(document.totals?.total || 0)} to {formatCurrency(document.meta?.outstanding_amount || 0)}
                      </p>
                    </div>
                  )}
                  
                  {document.meta?.status === "paid" && (
                    <div className="px-4 py-3 bg-green-50 border-t border-green-200">
                      <p className="text-sm text-green-800">
                        <span className="font-semibold">✓ Invoice fully paid</span> on {document.meta?.paid_at ? new Date(document.meta.paid_at).toLocaleDateString() : "N/A"}
                      </p>
                      {document.meta?.paid_via_receipt_number && (
                        <p className="text-xs text-green-700 mt-1">
                          Receipt: {document.meta.paid_via_receipt_number}
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* Record Payment Button for unpaid/partial invoices */}
                  {(document.meta?.status === "issued" || document.meta?.status === "partial" || document.meta?.status === "sent") && (
                    <div className="px-4 py-3 border-t">
                      <Button 
                        onClick={openPaymentDialog}
                        className="w-full bg-emerald-600 hover:bg-emerald-700"
                        data-testid="record-payment-btn"
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        Record Payment
                      </Button>
                    </div>
                  )}
                </div>
              )}
              
              {/* Payment Activity Log for Payment Invoices */}
              {docType === "payment-invoice" && paymentHistory.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Payment Activity</h4>
                  <div className="border rounded-lg overflow-hidden">
                    {paymentHistory.map((payment, idx) => (
                      <div 
                        key={idx} 
                        className={`px-4 py-3 text-sm ${idx > 0 ? "border-t" : ""} ${payment.is_final_payment ? "bg-green-50" : "bg-white"}`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-gray-900">
                              {payment.is_final_payment ? "Final Payment" : "Partial Payment"} Applied
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {new Date(payment.created_at).toLocaleDateString()} • {payment.payment_method}
                              {payment.payment_reference && ` • Ref: ${payment.payment_reference}`}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-green-600">+{formatCurrency(payment.payment_amount)}</p>
                            <p className="text-xs text-gray-500">Receipt: {payment.receipt_number}</p>
                          </div>
                        </div>
                        <p className="text-xs text-gray-600 mt-2 italic">
                          Invoice balance reduced from {formatCurrency(payment.outstanding_before)} to {formatCurrency(payment.outstanding_after)}.
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Donation Amount */}
              {docType === "donation-receipt" && (
                <div 
                  className="mb-6 p-4 rounded"
                  style={{ backgroundColor: `${document.brand_primary_color}10` }}
                >
                  <p className="text-sm text-gray-600 mb-1">Donation Amount:</p>
                  <p 
                    className="text-2xl font-bold"
                    style={{ color: document.brand_secondary_color }}
                  >
                    {formatCurrency(document.totals.total)}
                  </p>
                  {document.content.purpose && (
                    <p className="text-sm text-gray-600 mt-2">
                      Purpose: {document.content.purpose}
                    </p>
                  )}
                </div>
              )}

              {/* Letter Body */}
              {docType === "letter" && (
                <div className="mb-6">
                  {document.content.subject && (
                    <p 
                      className="font-semibold mb-4"
                      style={{ color: document.brand_primary_color }}
                    >Re: {document.content.subject}</p>
                  )}
                  <div className="whitespace-pre-line text-sm leading-relaxed">
                    {document.content.body || "Letter content will appear here..."}
                  </div>
                </div>
              )}

              {/* Meeting Minutes */}
              {docType === "meeting-minutes" && (
                <div className="mb-6 text-sm">
                  {document.content.title && (
                    <h2 
                      className="text-lg font-bold mb-4"
                      style={{ color: document.brand_primary_color }}
                    >
                      {document.content.title}
                    </h2>
                  )}
                  {document.content.attendees.length > 0 && (
                    <div className="mb-4">
                      <p className="font-semibold">Attendees:</p>
                      <p className="text-gray-600">{document.content.attendees.join(", ")}</p>
                    </div>
                  )}
                  {document.content.body && (
                    <div className="mb-4">
                      <p className="font-semibold">Minutes:</p>
                      <div className="whitespace-pre-line text-gray-600">{document.content.body}</div>
                    </div>
                  )}
                  {document.content.action_items.length > 0 && (
                    <div>
                      <p className="font-semibold">Action Items:</p>
                      <ul className="list-disc list-inside text-gray-600">
                        {document.content.action_items.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Payment Info */}
              {(docType === "receipt" || docType === "proof-of-payment") && document.content.payment_method && (
                <div 
                  className="mb-6 p-3 rounded text-sm"
                  style={{ backgroundColor: `${document.brand_primary_color}10` }}
                >
                  <p><span className="font-semibold">Payment Method:</span> {document.content.payment_method}</p>
                  {document.content.payment_reference && (
                    <p><span className="font-semibold">Reference:</span> {document.content.payment_reference}</p>
                  )}
                </div>
              )}

              {/* Notes */}
              {document.notes && (
                <div className="mb-4 text-xs">
                  <p className="font-semibold text-gray-700 mb-1 uppercase tracking-wide text-[10px]">Notes</p>
                  <p className="text-gray-600 whitespace-pre-line">{document.notes}</p>
                </div>
              )}

              {/* Terms */}
              {document.terms && (
                <div className="text-[10px] text-gray-400 border-t border-gray-100 pt-3 mt-4">
                  <p className="font-semibold mb-1 uppercase tracking-wide">Terms & Conditions</p>
                  <p className="whitespace-pre-line leading-relaxed">{document.terms}</p>
                </div>
              )}

              {/* Thank You */}
              {docType === "donation-receipt" && (
                <div 
                  className="mt-8 text-center text-sm"
                  style={{ color: document.brand_primary_color }}
                >
                  <p>Thank you for your generous donation!</p>
                </div>
              )}

              {/* Signature Section */}
              {document.signature_data && (
                <div className="mt-8 pt-4 border-t border-gray-200">
                  <div className="flex justify-end">
                    <div className="text-center">
                      <img 
                        src={document.signature_data}
                        alt="Signature"
                        className="h-12 object-contain mb-1"
                      />
                      <div 
                        className="h-px w-32 mb-1"
                        style={{ backgroundColor: document.brand_primary_color }}
                      />
                      <p className="text-xs text-gray-500">Authorized Signature</p>
                    </div>
                  </div>
                </div>
              )}
              </div>

              {/* Footer with brand bar */}
              {template !== "minimal" && (
                <div 
                  className="absolute bottom-0 left-0 right-0 h-1.5"
                  style={{ backgroundColor: document.brand_primary_color }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
